import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import PageWrapper from '../components/PageWrapper';
import {
  UserIcon,
  CogIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const Admin = () => {
  const { user, logout } = useContext(AuthContext);
  const { canAccessAdminFeatures } = usePermissions();

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-8 w-8 mr-3" />
                <h2 className="text-2xl font-bold">Admin Panel</h2>
              </div>
            </div>

            <div className="p-6">
              {canAccessAdminFeatures ? (
                <div className="space-y-6">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center">
                    <UserIcon className="h-6 w-6 text-emerald-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-900">
                        Welcome, {user?.given || user?.email}!
                      </h3>
                      <p className="text-emerald-700">You have administrative access to the system.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* User Management */}
                    <Link
                      to="/admin/users"
                      className="group bg-white border-2 border-gray-200 hover:border-emerald-400 rounded-lg p-6 text-center transition-colors"
                    >
                      <UserIcon className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">User Management</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Manage user accounts and role assignments
                      </p>
                      <span className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg group-hover:bg-emerald-700 transition-colors text-sm">
                        Open <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </span>
                    </Link>

                    {/* Role Management */}
                    <Link
                      to="/admin/roles"
                      className="group bg-white border-2 border-gray-200 hover:border-emerald-400 rounded-lg p-6 text-center transition-colors"
                    >
                      <ShieldCheckIcon className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Role Management</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Configure roles and permission assignments
                      </p>
                      <span className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg group-hover:bg-emerald-700 transition-colors text-sm">
                        Open <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </span>
                    </Link>

                    {/* Subscription Management */}
                    <Link
                      to="/admin/subscriptions"
                      className="group bg-white border-2 border-gray-200 hover:border-emerald-400 rounded-lg p-6 text-center transition-colors"
                    >
                      <CreditCardIcon className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Subscriptions</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Manage plan assignments for agencies and users
                      </p>
                      <span className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg group-hover:bg-emerald-700 transition-colors text-sm">
                        Open <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </span>
                    </Link>

                    {/* System Settings — placeholder */}
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">System Settings</h3>
                      <p className="text-gray-500 text-sm mb-4">
                        Configure system-wide settings and preferences
                      </p>
                      <span className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-600 rounded-lg text-sm cursor-not-allowed">
                        Coming Soon
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <button
                      onClick={logout}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center">
                  <ShieldCheckIcon className="h-8 w-8 text-red-500 mr-4" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">Access Denied</h3>
                    <p className="text-red-700">
                      System Administrator access is required to view this page.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Admin;

